/** Ruimte die een brede tooltip aan de vensterrand vrijhoudt. */
const VIEWPORT_MARGIN = 8

/**
 * @typedef {object} TriggerRect
 * @property {number} top - Bovenkant van de trigger, viewport-coordinaten
 * @property {number} bottom - Onderkant van de trigger, viewport-coordinaten
 * @property {number} left - Linkerkant van de trigger, viewport-coordinaten
 */

/**
 * @typedef {object} WideTooltipStyle
 * @property {number} left - Linkerpositie, altijd binnen het venster
 * @property {number} maxWidth - Breedte waarop de tooltip mag uitlopen
 * @property {number} [top] - Gezet als de tooltip onder de trigger hangt
 * @property {number} [bottom] - Gezet als de tooltip naar boven is geklapt
 * @property {number} [maxHeight] - Alleen gezet als hij nergens heel past
 */

/**
 * Bepaalt waar een brede tooltip (de inhoud van een notitie) komt te staan,
 * zodat hij altijd volledig in beeld valt.
 *
 * **Waarom dit een aparte functie is.** Dit stond als losse regels in
 * `Tooltip.jsx` en rekende met een vaste schatting van de hoogte
 * (`WIDE_TOOLTIP_HEIGHT = 180`). Een tooltip met de maximale notitie-inhoud is
 * op 420px breed in werkelijkheid 201px hoog. Bij 180 tot ~205px ruimte onder
 * de rij besloot de oude code daarom niet om te klappen, waarna de onderste
 * pixels buiten beeld vielen — inclusief de ' [...]' die de lezer moet
 * vertellen dat er meer is. `.tooltip` staat op `pointer-events: none`, dus die
 * staart was niet te scrollen en niet te selecteren: een halve notitie zag
 * eruit als een hele. Zie docs/inbox.md #150.
 *
 * De uitweg is niet een betere schatting maar geen schatting: de aanroeper
 * meet de tooltip nadat hij gerenderd is en geeft de echte hoogte mee.
 * AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment
 *
 * Past hij noch boven noch onder de trigger, dan overlapt hij de trigger liever
 * dan dat er tekst wegvalt: onleesbaar is erger dan in de weg.
 *
 * @param {object} params
 * @param {TriggerRect} params.trigger - Positie van het element waar de tooltip aan hangt
 * @param {number} params.tooltipHeight - Gemeten hoogte van de tooltip in px
 * @param {number} params.viewportWidth - Breedte van het venster
 * @param {number} params.viewportHeight - Hoogte van het venster
 * @param {number} params.maxWidth - Breedte waarop de tooltip mag uitlopen
 * @param {number} [params.left] - Horizontale verschuiving t.o.v. de trigger
 * @param {number} [params.margin] - Ruimte die aan de vensterrand vrijblijft
 * @returns {WideTooltipStyle}
 */
const positionWideTooltip = ({
    trigger,
    tooltipHeight,
    viewportWidth,
    viewportHeight,
    maxWidth,
    left = 0,
    margin = VIEWPORT_MARGIN,
}) => {
    /** @type {WideTooltipStyle} */
    const style = {
        maxWidth,
        // Staat de kolom rechts, dan zou de tooltip half buiten beeld vallen.
        left: Math.max(margin, Math.min(trigger.left + left, viewportWidth - maxWidth - margin)),
    }

    const spaceBelow = viewportHeight - trigger.bottom - margin
    const spaceAbove = trigger.top - margin

    if (tooltipHeight <= spaceBelow) {
        style.top = trigger.bottom

        return style
    }

    if (tooltipHeight <= spaceAbove) {
        // Vastzetten op de onderkant, zodat hij vanaf de trigger omhoog groeit.
        style.bottom = viewportHeight - trigger.top + margin

        return style
    }

    // Nergens genoeg ruimte: tegen de bovenrand en de hoogte begrenzen. Liever
    // de trigger overlappen dan de staart van de notitie kwijtraken.
    style.top = margin
    style.maxHeight = viewportHeight - margin * 2

    return style
}

export { VIEWPORT_MARGIN }
export default positionWideTooltip
